import { MARKET_API_URL } from "../config";
import React, { useState, useEffect, useContext, useCallback } from "react";
import axios from "axios";
import GeneralContext from "./GeneralContext";

const OptionChain = () => {
  const [symbol, setSymbol] = useState("NIFTY");
  const [expiry, setExpiry] = useState("30-JUL-2026");
  const [range, setRange] = useState(10); // ATM ± 10 default
  const [chainData, setChainData] = useState(null);
  const [loading, setLoading] = useState(true);

  const { openBuyWindow, openSellWindow } = useContext(GeneralContext);

  const fetchOptionChain = useCallback(async (isInitial = false) => {
    if (isInitial) setLoading(true);
    try {
      const res = await axios.get(`${MARKET_API_URL}/api/options/chain?symbol=${symbol}&expiry=${expiry}`);
      setChainData(res.data);
    } catch (err) {
      console.error("Error fetching option chain", err);
    } finally {
      if (isInitial) setLoading(false);
    }
  }, [symbol, expiry]);

  useEffect(() => {
    fetchOptionChain(true);
    const interval = setInterval(() => fetchOptionChain(false), 2500);
    return () => clearInterval(interval);
  }, [symbol, expiry, fetchOptionChain]);

  const allStrikes = chainData?.strikes || [];
  const atmStrike = chainData?.atm_strike || 0;

  // Filter strikes based on range selector (ATM ± N)
  const displayedStrikes = range === 999 ? allStrikes : allStrikes.filter((s) => {
    if (!atmStrike) return true;
    const step = symbol === "BANKNIFTY" ? 100 : 50;
    return Math.abs(s.strike - atmStrike) <= (range * step);
  });

  return (
    <div style={{ padding: "20px", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
      
      {/* Platform Header Disclaimer */}
      <div style={{ backgroundColor: "#e3f2fd", borderLeft: "4px solid #1976d2", padding: "10px 16px", borderRadius: "6px", marginBottom: "16px", fontSize: "12px", color: "#0d47a1" }}>
        ⚡ <strong>ANGEL ONE SMARTSTREAM 2.0 OPTION CHAIN</strong>: Real-time option ticks, OI change, Black-Scholes Greeks (&Delta;, &Theta;, &Gamma;, Vega), Volume PCR & Max Pain.
      </div>

      {/* Header Bar Controls */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", backgroundColor: "#ffffff", padding: "14px 20px", borderRadius: "8px", border: "1px solid #e0e0e0" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: "18px", color: "#333" }}>Live Options Chain & Greeks</h2>
          <div style={{ margin: "6px 0 0", color: "#666", fontSize: "13px", display: "flex", gap: "16px" }}>
            <span>Spot: <strong style={{ color: "#1976d2" }}>₹{chainData?.underlying_price?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || "0.00"}</strong></span>
            <span>ATM: <strong style={{ color: "#d81b60" }}>{chainData?.atm_strike || "--"}</strong></span>
            <span>Max Pain: <strong>₹{chainData?.max_pain}</strong></span>
            <span>OI PCR: <strong>{chainData?.pcr}</strong></span>
            <span>Vol PCR: <strong>{chainData?.vol_pcr}</strong></span>
          </div>
        </div>

        {/* Filter Controls */}
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          
          {/* Symbol Selector */}
          <select value={symbol} onChange={(e) => setSymbol(e.target.value)} style={selectStyle}>
            <option value="NIFTY">NIFTY 50</option>
            <option value="BANKNIFTY">BANK NIFTY</option>
            <option value="FINNIFTY">FIN NIFTY</option>
            <option value="MIDCPNIFTY">MIDCAP NIFTY</option>
            <option value="RELIANCE">RELIANCE</option>
          </select>

          {/* Expiry Selector */}
          <select value={expiry} onChange={(e) => setExpiry(e.target.value)} style={selectStyle}>
            <option value="30-JUL-2026">30-JUL-2026 (Weekly)</option>
            <option value="06-AUG-2026">06-AUG-2026</option>
            <option value="13-AUG-2026">13-AUG-2026</option>
            <option value="27-AUG-2026">27-AUG-2026 (Monthly)</option>
          </select>

          {/* Strike Range Selector */}
          <select value={range} onChange={(e) => setRange(Number(e.target.value))} style={selectStyle}>
            <option value={5}>ATM ± 5 Strikes</option>
            <option value={10}>ATM ± 10 Strikes</option>
            <option value={20}>ATM ± 20 Strikes</option>
            <option value={999}>Full Chain</option>
          </select>

          <button onClick={() => fetchOptionChain(true)} style={{ backgroundColor: "#1976d2", color: "#fff", border: "none", padding: "8px 14px", borderRadius: "4px", cursor: "pointer", fontWeight: "bold", fontSize: "12px" }}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {loading && !chainData ? (
        <p style={{ textAlign: "center", color: "#666" }}>Loading SmartAPI Option Chain & Greeks...</p>
      ) : (
        <div style={{ backgroundColor: "#ffffff", borderRadius: "8px", overflowX: "auto", boxShadow: "0 2px 8px rgba(0,0,0,0.05)", border: "1px solid #eee" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px", textAlign: "center" }}>
            <thead>
              <tr style={{ backgroundColor: "#f5f5f5", color: "#555", borderBottom: "2px solid #ddd" }}>
                <th colSpan={7} style={{ padding: "8px", backgroundColor: "#eef6ff", color: "#1976d2", fontSize: "13px" }}>CALL OPTIONS (CE)</th>
                <th style={{ padding: "8px", backgroundColor: "#eceff1", color: "#333", fontSize: "14px" }}>STRIKE</th>
                <th colSpan={7} style={{ padding: "8px", backgroundColor: "#fff3e0", color: "#e65100", fontSize: "13px" }}>PUT OPTIONS (PE)</th>
              </tr>
              <tr style={{ backgroundColor: "#fafafa", color: "#666", borderBottom: "1px solid #ddd" }}>
                <th style={thStyle}>Delta</th>
                <th style={thStyle}>Theta</th>
                <th style={thStyle}>IV (%)</th>
                <th style={thStyle}>Volume</th>
                <th style={thStyle}>OI Chg</th>
                <th style={thStyle}>LTP (₹)</th>
                <th style={thStyle}>Action</th>
                <th style={{ ...thStyle, backgroundColor: "#eceff1", fontWeight: "bold" }}>Strike Price</th>
                <th style={thStyle}>Action</th>
                <th style={thStyle}>LTP (₹)</th>
                <th style={thStyle}>OI Chg</th>
                <th style={thStyle}>Volume</th>
                <th style={thStyle}>IV (%)</th>
                <th style={thStyle}>Theta</th>
                <th style={thStyle}>Delta</th>
              </tr>
            </thead>
            <tbody>
              {displayedStrikes.map((s) => {
                const isATM = s.strike === atmStrike;
                return (
                  <tr key={s.strike} style={{ borderBottom: "1px solid #eee", backgroundColor: isATM ? "#fffde7" : "transparent" }}>
                    {/* CALLS */}
                    <td style={tdStyle}>{s.CE?.delta || "-"}</td>
                    <td style={tdStyle}>{s.CE?.theta || "-"}</td>
                    <td style={tdStyle}>{s.CE?.iv || "-"}%</td>
                    <td style={tdStyle}>{s.CE?.volume?.toLocaleString() || "-"}</td>
                    <td style={{ ...tdStyle, color: s.CE?.oiChange >= 0 ? "#2e7d32" : "#c62828" }}>
                      {s.CE?.oiChange >= 0 ? `+${s.CE?.oiChange?.toLocaleString()}` : s.CE?.oiChange?.toLocaleString()}
                    </td>
                    <td style={{ ...tdStyle, fontWeight: "bold", color: "#2e7d32" }}>₹{s.CE?.ltp || "-"}</td>
                    <td style={tdStyle}>
                      {s.CE && (
                        <div style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
                          <button onClick={() => openBuyWindow(s.CE.symbol)} style={buyBtnStyle}>B</button>
                          <button onClick={() => openSellWindow(s.CE.symbol)} style={sellBtnStyle}>S</button>
                        </div>
                      )}
                    </td>

                    {/* STRIKE */}
                    <td style={{ ...tdStyle, fontWeight: "bold", backgroundColor: isATM ? "#fff59d" : "#f5f5f5", fontSize: "13px" }}>
                      {s.strike} {isATM && <span style={{ fontSize: "10px", color: "#d81b60", marginLeft: "4px" }}>ATM</span>}
                    </td>

                    {/* PUTS */}
                    <td style={tdStyle}>
                      {s.PE && (
                        <div style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
                          <button onClick={() => openBuyWindow(s.PE.symbol)} style={buyBtnStyle}>B</button>
                          <button onClick={() => openSellWindow(s.PE.symbol)} style={sellBtnStyle}>S</button>
                        </div>
                      )}
                    </td>
                    <td style={{ ...tdStyle, fontWeight: "bold", color: "#c62828" }}>₹{s.PE?.ltp || "-"}</td>
                    <td style={{ ...tdStyle, color: s.PE?.oiChange >= 0 ? "#2e7d32" : "#c62828" }}>
                      {s.PE?.oiChange >= 0 ? `+${s.PE?.oiChange?.toLocaleString()}` : s.PE?.oiChange?.toLocaleString()}
                    </td>
                    <td style={tdStyle}>{s.PE?.volume?.toLocaleString() || "-"}</td>
                    <td style={tdStyle}>{s.PE?.iv || "-"}%</td>
                    <td style={tdStyle}>{s.PE?.theta || "-"}</td>
                    <td style={tdStyle}>{s.PE?.delta || "-"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const thStyle = { padding: "8px 6px", fontSize: "11px", textTransform: "uppercase" };
const tdStyle = { padding: "6px", fontSize: "12px", color: "#444" };
const selectStyle = { padding: "6px 12px", borderRadius: "4px", border: "1px solid #ccc", fontWeight: "bold", fontSize: "12px" };

const buyBtnStyle = {
  backgroundColor: "#4184f3",
  color: "#fff",
  border: "none",
  padding: "3px 7px",
  borderRadius: "3px",
  cursor: "pointer",
  fontWeight: "bold",
  fontSize: "11px",
};

const sellBtnStyle = {
  backgroundColor: "#ff5722",
  color: "#fff",
  border: "none",
  padding: "3px 7px",
  borderRadius: "3px",
  cursor: "pointer",
  fontWeight: "bold",
  fontSize: "11px",
};

export default OptionChain;
